import { attendance, rosters, shifts } from "../components/data/sampleData";

/* ======================================
   TIME UTILS
====================================== */
function timeToMinutes(t) {
  if (!t) return null;
  const m = t.match(/(\d+)(AM|PM)/i);
  if (!m) return null;

  let h = parseInt(m[1], 10);
  const p = m[2].toUpperCase();
  

  if (p === "PM" && h !== 12) h += 12;
  if (p === "AM" && h === 12) h = 0;

  return h * 60;
}

function normalizeDate(d) {
  if (!d) return null;
  if (d.match(/^\d{2}-\d{2}-\d{4}$/)) {
    const [dd, mm, yyyy] = d.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }
  return d;
}

function getWorkWindow(att) {
  if (att.status !== "PRESENT") return null;
  const def = shifts[att.shift];
  return {
    date: att.date,
    start: timeToMinutes(att.actualStart ?? def.start),
    end: timeToMinutes(att.actualEnd ?? def.end),
  };
}

/* ======================================
   ROSTER (MONTHLY → DAILY)
====================================== */
export function getRosteredShifts(employeeId, date) {
  const targetDate = new Date(date);

  return rosters.flatMap(roster => {
    const rosterMonth = new Date(roster.month);
    if (
      rosterMonth.getFullYear() !== targetDate.getFullYear() ||
      rosterMonth.getMonth() !== targetDate.getMonth()
    )
      return [];

    return roster.employees
      .filter(e => e.employeeId === employeeId)
      .map(e => ({
        shift: e.shift,
        siteId: roster.siteId,
      }));
  });
}

/* ======================================
   ATTENDANCE (PRESENT ONLY)
====================================== */
export function getWorkedShifts(employeeId, date) {
  const target = normalizeDate(date);
  return attendance.filter(
    a =>
      a.employeeId === employeeId &&
      normalizeDate(a.date) === target &&
      a.status === "PRESENT"
  );
}

export function getAttendanceStatus(employeeId, date) {
  const target = normalizeDate(date);
  const att = attendance.find(a => normalizeDate(a.date) === target && a.employeeId === employeeId);
  return att?.status || null; // "PRESENT" | "ABSENT" | undefined
}

export function getPrevDayAttendance(employeeId, date) {
  const d = new Date(date);
  d.setDate(d.getDate() - 1);
  const prev = d.toISOString().slice(0, 10);
  return attendance.filter(
    a =>
      a.employeeId === employeeId &&
      normalizeDate(a.date) === prev &&
      a.status === "PRESENT"
  );
}

/* ======================================
   BACK-TO-BACK DETECTION
====================================== */
export function detectBackToBack(employeeId, date) {
  const today = getWorkedShifts(employeeId, date);
  const prev = getPrevDayAttendance(employeeId, date);

  // Same-day low rest
  if (today.length > 1) {
    const windows = today
      .map(getWorkWindow)
      .filter(Boolean)
      .sort((a, b) => a.start - b.start);

    for (let i = 1; i < windows.length; i++) {
      if (windows[i].start - windows[i - 1].end < 120) return true;
    }
  }

  // Cross-day low rest (<10 hrs)
  for (const t of today) {
    const tWin = getWorkWindow(t);
    if (!tWin) continue;

    for (const p of prev) {
      const pWin = getWorkWindow(p);
      if (!pWin) continue;

      const rest = tWin.start + 1440 - pWin.end;
      if (rest < 600) return true;
    }
  }

  return false;
}

/* ======================================
   MAIN FATIGUE CALCULATOR
====================================== */
export function calculateFatigue(employeeId, date) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const targetDate = new Date(date);

  // FUTURE DATE
  if (date > todayStr) {
    return buildResult("nodata", {}, [], []);
  }

  const rostered = getRosteredShifts(employeeId, date);
  const worked = getWorkedShifts(employeeId, date);
  const attStatus = getAttendanceStatus(employeeId, date);

  const rosteredShifts = rostered.map(r => r.shift);
  const workedShifts = worked.map(w => w.shift);

  const extraShift = workedShifts.length > rosteredShifts.length;
  const doubleShift = workedShifts.length > 1;
  const rosterViolation =
    workedShifts.length > 0 && rosteredShifts.length === 0;

  const backToBack = detectBackToBack(employeeId, date);

  // CASE: No roster, no attendance
  if (rosteredShifts.length === 0 && !workedShifts.length && !attStatus) {
    return buildResult("nodata", {}, [], []);
  }

  // CASE: Roster exists but no attendance or explicitly ABSENT
  if ((rosteredShifts.length && (!workedShifts.length || attStatus === "ABSENT"))) {
    return buildResult("off", {}, rosteredShifts, workedShifts);
  }

  // HIGH fatigue
  if (doubleShift || extraShift || backToBack)
    return buildResult(
      "high",
      { doubleShift, extraShift, backToBack, rosterViolation },
      rosteredShifts,
      workedShifts
    );

  // MEDIUM fatigue
  if (rosterViolation)
    return buildResult(
      "medium",
      { rosterViolation },
      rosteredShifts,
      workedShifts
    );

  // NORMAL
  if (workedShifts.length)
    return buildResult("normal", {}, rosteredShifts, workedShifts);

  // OFF (fallback, should not hit due to earlier checks)
  return buildResult("off", {}, rosteredShifts, workedShifts);
}

/* ======================================
   RESULT BUILD
====================================== */
function buildResult(level, flags, rosteredShifts, workedShifts) {
  const meta = {
    high: { icon: "🔥", label: "HIGH" },
    medium: { icon: "⚠️", label: "UNPLANNED" },
    normal: { icon: "🟢", label: "OK" },
    off: { icon: "⚪", label: "ABSENT" },
    nodata: { icon: "-", label: "NODATA" },
  };

  return {
    level,
    icon: meta[level].icon,
    label: meta[level].label,
    tooltip: buildTooltip(flags),
    rosteredShifts,
    workedShifts,
    flags: {
      extraShift: false,
      doubleShift: false,
      rosterViolation: false,
      backToBack: false,
      ...flags,
    },
  };
}

function buildTooltip(flags) {
  const reasons = [];
  if (flags.doubleShift) reasons.push("Multiple shifts same day");
  if (flags.extraShift) reasons.push("Extra shift beyond roster");
  if (flags.backToBack) reasons.push("Insufficient rest between shifts");
  if (flags.rosterViolation) reasons.push("Worked without roster");
  return reasons.join(" • ") || "No issues";
}
